/**
 * GuaranteeAttentionChip — RC7-IA-026.
 *
 * Compact "N guarantees need attention" chip on the Dashboard.
 * Consumes the existing `/api/guarantees` `summary.counts` block
 * ONLY — no new endpoint, no new persistence, no derived state.
 *
 * Semantics:
 *   · outstanding  — actionable (something the traveller is owed)
 *   · disputed     — actionable (contested by the traveller)
 *   · expired      — actionable (subset of outstanding whose deadline
 *                    has passed; already counted separately)
 *
 * We surface a chip iff `outstanding + disputed > 0`. Never renders
 * for a summary with no actionable state — the Dashboard stays quiet
 * when the ledger is quiet (per PO directive: "Do not invent an
 * attention state merely to create a Dashboard card.").
 *
 * Click → Add-Ons workspace (canonical presentation owner post
 * RC7-IA-026 · 2026-02-19).
 */
import React, { useEffect, useState } from "react";
import { AlertOctagon, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCruise } from "@/context/CruiseContext";

export default function GuaranteeAttentionChip() {
  const { setRoute } = useCruise();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/guarantees");
        if (!alive) return;
        setState({ loading: false, summary: r.data?.summary || null });
      } catch {
        if (alive) setState({ loading: false, summary: null });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading) return null;

  const counts = state.summary?.counts || {};
  const actionable = (counts.outstanding || 0) + (counts.disputed || 0);
  if (actionable <= 0) return null;

  const label = actionable === 1
    ? "1 guarantee needs attention"
    : `${actionable} guarantees need attention`;

  return (
    <div className="mt-6 px-4 sm:px-6" data-testid="guarantee-attention-chip">
      <button
        type="button"
        onClick={() => setRoute("add-ons")}
        className="w-full flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors px-4 py-3 text-left"
        aria-label={`${label} · open Add-Ons ledger`}
      >
        <span className="shrink-0 w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
          <AlertOctagon className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-display text-[14px] font-semibold text-amber-900 leading-tight">
            {label}
          </span>
          <span className="block font-body text-[12px] text-amber-700 mt-0.5">
            Open the Guarantee Ledger in Add-Ons
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-amber-500 shrink-0" aria-hidden />
      </button>
    </div>
  );
}
